import * as React from "react";
import { lazyComponent } from "./lazy";
import { IContextProvider, registerUI } from "./uxp";

// Eager, at bundle eval: register all built-in template metadata (→ registerWidget)
// and field types. This is the widget-render contract — the SAME bundle renders
// saved widgets on pages/dashboards WITHOUT the editor open, so registration must
// happen here, not inside the now-lazy editor chunk. Each template's heavy render
// component is code-split (see its *.register.ts → lazy *View.tsx).
import "./components/templates/templates";
import "./components/common/ui-fields/uiFields";

// The bundle's English strings, registered with the host UXP localization
// registry (window.registerLocalization) after component registration below.
import LocalizationMessages from '../localization.json';

// The editor is only needed when a user opens the designer, not to render widgets.
// Lazy-load it (with WidgetEditorPage) into a single "editor" chunk, pulling the
// datasource-picker, action-editor, dnd-kit and jsonpath out of the initial
// bundle. lazyComponent wraps its own <Suspense>, so registerUI needs no extra.
const DesignerComponent = lazyComponent(
    () => import(/* webpackChunkName: "editor" */ "./components/designer/DesignerComponent")
);
const WidgetEditorPage = lazyComponent(
    () => import(/* webpackChunkName: "editor" */ "./components/designer/v5/WidgetEditorPage")
);
import { debounce, extractContextData, formatNumber, generateUUID, getQueryVariable, getUrlFriendlyString, hasValue, loadInitialData, replaceWithData, tryParseJSON } from "./util";
import { findCardTemplate, generateWidgetFromJSON, getCardTemplates } from "./components/common/common";
import TemplateSelector from "./components/designer/TemplateSelector";
import { SelectableItem, selectUIProps } from "./components/common/SelectableItem";
import { SimpleTextProperty } from "./components/common/ui-props/SimpleTextProperty";
import WDFilterPanel from "./components/common/components/WDFilterPanel";
import { createDefaultPropsForWidgetWrapperWithSidebar, WidgetWrapperWithSidebar } from "./components/common/widget-wrappers/WidgetWrapperWithSidebar";
import ActionButton from "./components/common/components/ActionButton";
import ActionRenderComponent from "./components/common/components/ActionRenderComponent";
import { ACTextProperty } from "./components/common/ui-props/ACTextProperty";
import { ActionProperty } from "./components/common/ui-props/ActionProperty";
import { BooleanGroupProperty } from "./components/common/ui-props/BooleanGroupProperty";
import { BooleanProperty } from "./components/common/ui-props/BooleanProperty";
import { ButtonProperty } from "./components/common/ui-props/ButtonProperty";
import { ColorProperty } from "./components/common/ui-props/ColorProperty";
import { ContainerProperty, extractContainerProperties } from "./components/common/ui-props/ContainerProperty";
import { DataSourceProperty } from "./components/common/ui-props/DataSourceProperty";
import { DropdownProperty } from "./components/common/ui-props/DropdownProperty";
import { HTMLCodeProperty } from "./components/common/ui-props/HTMLCodeProperty";
import { IconProperty } from "./components/common/ui-props/IconProperty";
import { ModelProperty } from "./components/common/ui-props/ModelProperty";
import { MultipleImagesProperty } from "./components/common/ui-props/MultitpleImagesProperty";
import { RichTextProperty } from "./components/common/ui-props/RichTextProperty";
import { SimpleDateProperty } from "./components/common/ui-props/SimpleDateProperty";
import { SimpleNumberProperty } from "./components/common/ui-props/SimpleNumberProperty";
import { TextFieldProperty } from "./components/common/ui-props/TextFieldProperty";
import { TextProperty, extractTextProperties } from "./components/common/ui-props/TextProperty";
import { VerticalAlignmentProperty } from "./components/common/ui-props/VerticalAlignmentProperty";
import { FieldContainer } from "./components/common/FieldContainer";
import { getQueryBuilder } from "./components/common/data-source-picker/source-picker-helper";
import { LucyQueryBuilderDataSourcePicker } from "./components/common/data-source-picker/lucy-query-builder/LucyQueryBuilderSourceEditor";
import { appendRowToContextData, useInitialContextData } from "./hooks/useContextData";
import { executeAndBind, processInitialSource } from "./components/common/data-source-picker/datasource";
import { ColorsProperty, extractColorByLabelOrIndex } from "./components/common/ui-props/ColorsProperty";
import { roundOffValue } from "./components/common/ui-props/RoundOffProperty";
import { extractIconButtonProperties, IconButtonProperty } from "./components/common/ui-props/IconButtonProperty";
import { generateIconProp, generateIconPropAsSting } from "./components/common/icon-selector/IconEditor";
import { useRefreshDataSources } from "./components/common/data-source-picker/lucy-query-builder/useQEServices";


(window as any).WidgetDesignerComponents = {
    TemplateSelector: TemplateSelector,
    getTemplates: getCardTemplates,
    findTemplateById: findCardTemplate,
    generateWidgetFromJSON: generateWidgetFromJSON,

    // query builder related
    getQueryBuilder: getQueryBuilder,
    LucyQueryBuilderDataSourcePicker: LucyQueryBuilderDataSourcePicker,
    useRefreshDataSources: useRefreshDataSources,

    // components and functions required to build custom widget templates 
    WidgetDesignerFilterPanel: WDFilterPanel,
    WidgetWrapperWithTitleBar: WidgetWrapperWithSidebar,
    FieldContainer: FieldContainer,
    ActionButton: ActionButton,
    ActionRenderComponent: ActionRenderComponent,

    SelectableItem: SelectableItem,
    selectUIProps: selectUIProps,

    AutoCompleteTextProperty: ACTextProperty,
    ActionProperty: ActionProperty,
    BooleanGroupProperty: BooleanGroupProperty,
    BooleanProperty: BooleanProperty,
    ButtonProperty: ButtonProperty,
    IconButtonProperty: IconButtonProperty,
    ColorProperty: ColorProperty,
    ColorsProperty: ColorsProperty,
    ContainerProperty: ContainerProperty,
    DataSourceProperty: DataSourceProperty,
    DropdownProperty: DropdownProperty,
    HTMLCodeProperty: HTMLCodeProperty,
    IconProperty: IconProperty,
    ModelProperty: ModelProperty,
    MultipleImagesProperty: MultipleImagesProperty,
    RichTextProperty: RichTextProperty,
    SimpleDateProperty: SimpleDateProperty,
    SimpleNumberProperty: SimpleNumberProperty,
    SimpleTextProperty: SimpleTextProperty,
    TextFieldProperty: TextFieldProperty,
    TextProperty: TextProperty,
    VerticalAlignmentProperty: VerticalAlignmentProperty,


    extractContainerProperties: extractContainerProperties,
    extractTextProperties: extractTextProperties,
    extractColorByLabelOrIndex: extractColorByLabelOrIndex,
    extractIconButtonProperties: extractIconButtonProperties,
    hasValue: hasValue,
    tryParseJSON: tryParseJSON,
    generateUUID: generateUUID,
    debounce: debounce,
    getUrlFriendlyString: getUrlFriendlyString,
    loadInitialData: loadInitialData,
    extractContextData: extractContextData,
    formatNumber: formatNumber,

    useInitialContextData: useInitialContextData,
    processInitialSource: processInitialSource,
    executeAndBind: executeAndBind,
    createDefaultPropsForWidgetWrapperWithSidebar: createDefaultPropsForWidgetWrapperWithSidebar,
    replaceWithData: replaceWithData,
    roundOffValue: roundOffValue,
    appendRowToContextData: appendRowToContextData,
    generateIconPropAsSting:generateIconPropAsSting,
    generateIconProp:generateIconProp,

}


export interface IDefaultUIProps {
    uxpContext: IContextProvider,

}

const WidgetDesigner: React.FunctionComponent<IDefaultUIProps> = props => {
    const [modelKey, setModelKey] = React.useState(getQueryVariable('model'));
    const [initialSource, setinitialSource] = React.useState(getQueryVariable('init'));
    let designerProps = { ...props, modelKey, initialSource };
    return <DesignerComponent {...designerProps} />
}


registerUI({
    id: "widget-designer",
    component: WidgetDesigner,
    showDefaultHeader: false
});

// v5 full-page widget editor shell (Phase 5a). Props arrive via ComponentRenderer
// additionalProps, with URL search params as a fallback (handled inside the page).
registerUI({
    id: "widget-editor",
    component: WidgetEditorPage,
    showDefaultHeader: false
});

// Register the bundle's English localization strings with the host. Guarded:
// window.registerLocalization is absent when the bundle loads standalone or in
// older hosts (and in jsdom tests, where it is a no-op stub).
if (typeof (window as any).registerLocalization === 'function') {
    (window as any).registerLocalization(LocalizationMessages);
}